import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Seal } from "@/components/brand/Seal";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { addStrategistClient, updateStrategistClient } from "@/lib/strategist.functions";

interface Row {
  id: string;
  user_id: string;
  name: string;
  notes: string | null;
  policies: number;
  coverage: number;
  last_activity: string | null;
}

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const Route = createFileRoute("/agent/clients")({
  head: () => ({ meta: [{ title: "My Clients — Strategist Portal" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <AgentClients />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AgentClients() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState<Row | null>(null);
  const [policies, setPolicies] = useState<any[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [draftNotes, setDraftNotes] = useState("");
  const addFn = useServerFn(addStrategistClient);
  const updateFn = useServerFn(updateStrategistClient);

  useEffect(() => { if (user) void load(); }, [user]);

  async function load() {
    if (!user) return;
    const { data: cli } = await supabase.from("clients").select("id, user_id, notes, updated_at").eq("assigned_agent_id", user.id);
    const ids = (cli ?? []).map((c: any) => c.user_id);
    const [prof, pol] = await Promise.all([
      ids.length ? supabase.from("profiles").select("id, full_name").in("id", ids) : Promise.resolve({ data: [] }),
      cli?.length ? supabase.from("policies").select("client_id, face_amount, status").in("client_id", (cli as any).map((c: any) => c.id)) : Promise.resolve({ data: [] }),
    ]);
    const pm = new Map<string, string>();
    for (const p of (prof.data ?? []) as Array<{ id: string; full_name: string | null }>) pm.set(p.id, p.full_name || "Client");
    const polMap = new Map<string, { count: number; coverage: number }>();
    for (const p of (pol.data ?? []) as Array<{ client_id: string; face_amount: number | null; status: string }>) {
      const c = polMap.get(p.client_id) ?? { count: 0, coverage: 0 };
      if (p.status === "active") { c.count += 1; c.coverage += Number(p.face_amount) || 0; }
      polMap.set(p.client_id, c);
    }
    setRows(((cli ?? []) as any[]).map(c => ({
      id: c.id, user_id: c.user_id, name: pm.get(c.user_id) || "Client", notes: c.notes ?? null,
      policies: polMap.get(c.id)?.count ?? 0, coverage: polMap.get(c.id)?.coverage ?? 0,
      last_activity: c.updated_at,
    })));
  }

  async function openDrawer(r: Row) {
    setDrawer(r);
    setDraftNotes(r.notes ?? "");
    const { data } = await supabase.from("policies").select("id, carrier, product_type, policy_number, face_amount, status").eq("client_id", r.id);
    setPolicies(data ?? []);
  }

  async function handleAdd() {
    if (!form.fullName.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await addFn({ data: {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
      }});
      toast.success("Client added — invite sent to their inbox");
      setAddOpen(false);
      setForm({ fullName: "", email: "", phone: "", notes: "" });
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Could not add client");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveNotes() {
    if (!drawer) return;
    setSubmitting(true);
    try {
      await updateFn({ data: { clientId: drawer.id, notes: draftNotes } });
      toast.success("Notes saved");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save");
    } finally {
      setSubmitting(false);
    }
  }

  const filtered = useMemo(() => rows.filter(r => !q || r.name.toLowerCase().includes(q.toLowerCase())), [rows, q]);

  return (
    <PortalPage eyebrow="Households" title="My Clients" intro={`${rows.length} household${rows.length === 1 ? "" : "s"} assigned to you.`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Input placeholder="Search by name" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" />
        <PrimaryButton type="button" onClick={() => setAddOpen(true)}>+ Add Client</PrimaryButton>
      </div>
      {rows.length === 0 ? (
        <div className="bg-cream-50 border border-bronze-600/30 rounded-[4px] p-10 text-center">
          <Seal title="0" subtitle="Households" size="sm" />
          <h3 className="mt-4 font-display text-navy-800 text-[22px]">No assigned clients yet</h3>
          <p className="mt-2 text-charcoal-500 text-[14px]">Add a client directly, or wait for leads to convert and the agency to assign them to you.</p>
          <div className="mt-5"><PrimaryButton type="button" onClick={() => setAddOpen(true)}>+ Add Your First Client</PrimaryButton></div>
        </div>
      ) : (
        <div className="bg-white border border-bronze-600/20 rounded-[4px] overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-left text-charcoal-500 font-sans uppercase tracking-[0.12em] text-[10px]">
              <tr className="border-b border-bronze-600/20"><th className="px-4 py-3">Name</th><th className="px-4 py-3">Active Policies</th><th className="px-4 py-3">Total Coverage</th><th className="px-4 py-3">Last Activity</th></tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} onClick={() => openDrawer(r)} className="border-b border-bronze-600/10 hover:bg-cream-50 cursor-pointer">
                  <td className="px-4 py-3 font-medium text-navy-800">{r.name}</td>
                  <td className="px-4 py-3">{r.policies}</td>
                  <td className="px-4 py-3">{fmt(r.coverage)}</td>
                  <td className="px-4 py-3 text-charcoal-500">{r.last_activity ? new Date(r.last_activity).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent>
          <SheetHeader><SheetTitle className="font-display text-navy-800">{drawer?.name}</SheetTitle></SheetHeader>
          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-[0.22em] text-bronze-600 font-semibold">Policies</p>
            {policies.length === 0 ? <p className="mt-3 text-charcoal-500 text-[13px]">No policies on record yet.</p> : (
              <ul className="mt-3 space-y-3">{policies.map(p => (
                <li key={p.id} className="border-l-2 border-bronze-600/40 pl-3">
                  <p className="font-medium text-navy-800 text-[14px]">{p.carrier ?? "—"} · {p.product_type ?? "—"}</p>
                  <p className="text-charcoal-500 text-[12px]">#{p.policy_number ?? "pending"} · {fmt(Number(p.face_amount) || 0)} · {p.status}</p>
                </li>
              ))}</ul>
            )}
          </div>
          <div className="mt-8">
            <p className="text-[10px] uppercase tracking-[0.22em] text-bronze-600 font-semibold">Advisor Notes</p>
            <Textarea value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} rows={5} className="mt-2" placeholder="Touchpoints, follow-ups, family details…" maxLength={2000} />
            <div className="mt-3 flex justify-end">
              <PrimaryButton type="button" onClick={handleSaveNotes} disabled={submitting}>{submitting ? "Saving…" : "Save Notes"}</PrimaryButton>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-navy-800">Add a Client</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-charcoal-500">
            We'll create their household record and email them a secure invite to claim their client portal.
          </p>
          <div className="mt-4 space-y-3">
            <div>
              <Label htmlFor="cl-name">Full name</Label>
              <Input id="cl-name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} maxLength={200} />
            </div>
            <div>
              <Label htmlFor="cl-email">Email</Label>
              <Input id="cl-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={320} />
            </div>
            <div>
              <Label htmlFor="cl-phone">Phone (optional)</Label>
              <Input id="cl-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} maxLength={40} />
            </div>
            <div>
              <Label htmlFor="cl-notes">Notes (optional)</Label>
              <Textarea id="cl-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} maxLength={2000} />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <PrimaryButton type="button" onClick={handleAdd} disabled={submitting}>{submitting ? "Adding…" : "Add & Send Invite"}</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalPage>
  );
}
